// Phase E — Dashboard Pin Picker Modal
// Lets the user toggle which widgets appear on their dashboard. Only
// widgets the user has *permission* to view are shown in the list.

import { useState, useEffect } from "react";
import { X, Pin, PinOff, RotateCcw, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { WIDGETS, loadPins, savePinsSynced, availableWidgets } from "@/lib/dashboardWidgets";
import { toast } from "sonner";

export default function DashboardPinPicker({ open, onClose, onChange }) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [pins, setPins] = useState(() => loadPins(user));

  useEffect(() => {
    if (open) setPins(loadPins(user));
  }, [open, user]);

  const allowed = availableWidgets(user, can);

  function toggle(id) {
    setPins((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetToDefaults() {
    const def = new Set(WIDGETS.filter((w) => w.defaultRoles.includes(user?.role)).map((w) => w.id));
    setPins(def);
  }

  async function handleSave() {
    const res = await savePinsSynced(user, pins);
    if (!res.ok) {
      toast.error("Layout konnte nicht gespeichert werden — Browser-Speicher voll oder deaktiviert.");
      return;
    }
    onChange?.(pins);
    if (res.synced) {
      toast.success("Dashboard-Layout gespeichert · auf allen Geräten synchronisiert");
    } else {
      toast.success("Dashboard-Layout lokal gespeichert", {
        description: "Server-Sync fehlgeschlagen — wird beim nächsten Save erneut versucht.",
      });
    }
    onClose?.();
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
         data-testid="dashboard-pin-picker">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl flex flex-col">
        <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pin className="w-4 h-4 text-amber-400" />
            <h2 className="font-display text-lg font-semibold text-zinc-50">Dashboard anpassen</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="dashboard-pin-close"
            className="text-zinc-500 hover:text-zinc-200 p-1 rounded"
          ><X className="w-4 h-4" /></button>
        </header>

        <div className="px-5 py-3 text-xs text-zinc-500 border-b border-zinc-800/50 bg-zinc-950/50">
          Wähle, welche Karten auf deinem Dashboard erscheinen sollen. Es werden
          nur Karten angezeigt, für die du die Berechtigung hast. Hero, KPI-Leiste
          und Quick-Actions bleiben immer sichtbar.
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {allowed.length === 0 ? (
            <div className="text-center py-8 text-sm text-zinc-500">
              Keine zusätzlichen Widgets verfügbar.
            </div>
          ) : allowed.map((w) => {
            const pinned = pins.has(w.id);
            return (
              <button
                key={w.id}
                onClick={() => toggle(w.id)}
                data-testid={`dashboard-pin-toggle-${w.id}`}
                className={`w-full flex items-start gap-3 text-left px-4 py-3 rounded-md border transition-colors ${
                  pinned
                    ? "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10"
                    : "border-zinc-800 bg-zinc-950 hover:bg-zinc-900"
                }`}
              >
                <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  pinned ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-500"
                }`}>
                  {pinned ? <Check className="w-3 h-3" /> : <PinOff className="w-3 h-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${pinned ? "text-amber-200" : "text-zinc-100"}`}>
                    {w.label}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">{w.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between gap-3 bg-zinc-950/50">
          <button
            onClick={resetToDefaults}
            data-testid="dashboard-pin-reset"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 rounded"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Auf Standard zurücksetzen
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              data-testid="dashboard-pin-cancel"
              className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100 rounded"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              data-testid="dashboard-pin-save"
              className="px-4 py-1.5 text-sm bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold rounded"
            >
              {pins.size} aktiv · Speichern
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
